import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

const STORAGE_PREFIX = 'musa:';

export function persistedStorageKey(key: string) {
    return key.startsWith(STORAGE_PREFIX) ? key : `${STORAGE_PREFIX}${key}`;
}

function readStored<T>(key: string, defaultValue: T): T {
    try {
        const raw = sessionStorage.getItem(persistedStorageKey(key));
        if (raw == null) {
            return defaultValue;
        }
        return JSON.parse(raw) as T;
    } catch {
        return defaultValue;
    }
}

function writeStored<T>(key: string, value: T) {
    try {
        sessionStorage.setItem(persistedStorageKey(key), JSON.stringify(value));
    } catch {
        // Private browsing or full storage — UI still updates
    }
}

/**
 * Like useState, but also saves the value in sessionStorage as JSON under a musa: key.
 * The tab forgets it when closed. If storage is blocked or the JSON is bad,
 * we just use defaultValue. Changing `key` reloads that key's stored value.
 */
export function usePersistedState<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
    const defaultRef = useRef(defaultValue);
    defaultRef.current = defaultValue;

    const [cache, setCache] = useState(() => ({ key, value: readStored(key, defaultValue) }));

    if (cache.key !== key) {
        setCache({ key, value: readStored(key, defaultRef.current) });
    }

    const value = cache.key === key ? cache.value : readStored(key, defaultRef.current);

    const setPersisted = useCallback(
        (next: SetStateAction<T>) => {
            setCache((prev) => {
                const base = prev.key === key ? prev.value : readStored(key, defaultRef.current);
                const resolved = typeof next === 'function' ? (next as (prev: T) => T)(base) : next;
                writeStored(key, resolved);
                return { key, value: resolved };
            });
        },
        [key],
    );

    return [value, setPersisted];
}

/** Drop musa: sessionStorage keys for this tab. Call on logout and organization switch so filters do not leak across users or orgs. */
export function clearPersistedState() {
    try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const storedKey = sessionStorage.key(i);
            if (storedKey?.startsWith(STORAGE_PREFIX)) {
                keys.push(storedKey);
            }
        }
        keys.forEach((storedKey) => sessionStorage.removeItem(storedKey));
    } catch {
        // Private browsing or blocked storage
    }
}
